import { useState } from 'react';
import { Sun, Files, CheckSquare, Bell, DotsThreeOutline, X, CalendarCheck, HouseLine, Star, Siren, ClockCounterClockwise, GearSix, Sparkle, Microphone, SquaresFour } from '@phosphor-icons/react';

const PRIMARY_TABS = [
  { id: 'mobile-today',    label: 'Today',    icon: Sun },
  { id: 'mobile-pipeline', label: 'Pipeline', icon: Files },
  { id: 'mobile-tasks',    label: 'Tasks',    icon: CheckSquare },
  { id: 'mobile-updates',  label: 'Updates',  icon: Bell },
];

const MORE_ITEMS = [
  { id: 'dashboard',            label: 'Command Center',       icon: SquaresFour },
  { id: 'clara',                label: 'Clara AI',             icon: Sparkle },
  { id: 'recap',                label: 'Recap',                icon: Microphone },
  { id: 'commitment-dates',     label: 'Commitment Dates',     icon: CalendarCheck },
  { id: 'sales-contingencies',  label: 'Sales Contingencies',  icon: HouseLine },
  { id: 'escalations',          label: 'Escalations',          icon: Siren },
  { id: 'reviews',              label: 'Reviews',              icon: Star },
  { id: 'history',              label: 'History',              icon: ClockCounterClockwise },
];

export default function BottomNav({ activeTab, onNavigate, isAdmin, unreadCount = 0 }) {
  const [moreOpen, setMoreOpen] = useState(false);
  const moreItems = isAdmin ? [...MORE_ITEMS, { id: 'admin', label: 'Admin', icon: GearSix }] : MORE_ITEMS;
  const inMore = moreItems.some(i => i.id === activeTab);

  const handleTap = (id) => { onNavigate(id); setMoreOpen(false); };

  return (
    <>
      {/* Bottom tab bar - mobile only (<xl = <1280px). Protected from desktop. */}
      <nav
        className="xl:hidden fixed bottom-0 left-0 right-0 z-40 bg-card border-t border-border shadow-[0_-2px_12px_rgba(11,31,74,0.06)]"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0)' }}
        data-testid="bottom-nav"
      >
        <div className="grid grid-cols-5">
          {PRIMARY_TABS.map(t => {
            const active = activeTab === t.id;
            const showBadge = t.id === 'mobile-updates' && unreadCount > 0;
            return (
              <button key={t.id} onClick={() => handleTap(t.id)}
                data-testid={`bottom-tab-${t.id}`}
                className="relative flex flex-col items-center justify-center py-2 px-1 min-h-[56px] transition-colors active:bg-muted/40">
                <div className="relative">
                  <t.icon size={22} weight={active ? 'fill' : 'regular'} className={active ? 'text-[color:var(--mr-red)]' : 'text-muted-foreground'} />
                  {showBadge && (
                    <span className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 bg-[color:var(--mr-red)] text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </div>
                <span className={`text-[10px] mt-0.5 font-semibold ${active ? 'text-[color:var(--mr-red)]' : 'text-muted-foreground'}`}>{t.label}</span>
                {active && <span className="absolute top-0 left-1/2 -translate-x-1/2 w-6 h-0.5 rounded-full mr-red-bg" />}
              </button>
            );
          })}
          <button onClick={() => setMoreOpen(true)}
            data-testid="bottom-tab-more"
            className="relative flex flex-col items-center justify-center py-2 px-1 min-h-[56px] active:bg-muted/40">
            <DotsThreeOutline size={22} weight={inMore ? 'fill' : 'regular'} className={inMore ? 'text-[color:var(--mr-red)]' : 'text-muted-foreground'} />
            <span className={`text-[10px] mt-0.5 font-semibold ${inMore ? 'text-[color:var(--mr-red)]' : 'text-muted-foreground'}`}>More</span>
            {inMore && <span className="absolute top-0 left-1/2 -translate-x-1/2 w-6 h-0.5 rounded-full mr-red-bg" />}
          </button>
        </div>
      </nav>

      {/* More bottom-sheet */}
      {moreOpen && (
        <div className="xl:hidden fixed inset-0 z-50 flex items-end" data-testid="more-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMoreOpen(false)} />
          <div className="relative w-full bg-card rounded-t-2xl border-t border-border animate-fade-up" style={{ paddingBottom: 'env(safe-area-inset-bottom, 12px)' }}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <h3 className="text-sm font-bold text-foreground">More</h3>
              <button onClick={() => setMoreOpen(false)} data-testid="close-more-drawer" className="p-1 text-muted-foreground"><X size={20} /></button>
            </div>
            <div className="grid grid-cols-3 gap-2 p-4">
              {moreItems.map(it => {
                const active = activeTab === it.id;
                return (
                  <button key={it.id} onClick={() => handleTap(it.id)} data-testid={`more-tab-${it.id}`}
                    className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border transition-colors ${
                      active ? 'border-[color:var(--mr-red)] bg-[color:var(--mr-red-wash)]' : 'border-border bg-muted/30'
                    }`}>
                    <it.icon size={22} weight={active ? 'fill' : 'regular'} className={active ? 'text-[color:var(--mr-red-strong)]' : 'text-muted-foreground'} />
                    <span className={`text-[11px] font-semibold text-center leading-tight ${active ? 'text-[color:var(--mr-red-strong)]' : 'text-foreground'}`}>{it.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
